import React, { useContext, useRef, useState, useEffect } from 'react';
import { FormattedMessage, FormattedNumber, useIntl } from 'react-intl';
import { Alert, Spinner } from 'reactstrap';
import {
  calculateCartTotal,
  getActiveOrderConfig,
  getLocalizedName,
  getOrderErrorMessage,
  getOrderItemTotal,
  getTipSettings,
} from '../../../../utility/util/util.service';
import ModalSheet from '../modal-sheet/modal-sheet.component';
import { connect, useStore } from 'react-redux';
import {
  setCart,
  setCheckout,
  setCheckoutBackup,
  setOrder,
  setOrderHash,
} from '../../../../redux/store/store.actions';
import {
  setIsCheckoutModalOpen,
  setIsTableCartModalOpen,
  setIsTipModalOpen,
} from '../../../../redux/modal/modal.actions';
import classnames from 'classnames';
import { Grid } from 'react-feather';
import {
  CHECKOUT_METHOD_PAY_AND_GO,
  ORDER_TYPE_INHOUSE,
} from '../../../../utility/constants';
import { fetchOnAuthenticated } from '../../../../redux/me/me.actions';
import { notificationFormattedMessage } from '../../../../utility/notification/notification.service';
import LazyImage from '../../lazyload/lazy-image/lazy-image.component';
import * as Icon from 'react-feather';
import { createOrder } from './table-cart.service';
import { setSpecificOrder } from '../../../../redux/me/me.actions';
import { SocketContext } from '../../../../context/socket.context';
import { getCustomerProfile, updateTableCart } from '../../../../utility/sb-api/sb-api.service';
import { AvForm } from 'availity-reactstrap-validation';
import SaveSubmitButton from '../../form/buttons/SaveSubmitButton';
import Marquee from '../../helpers/marquee.component';

const TableCartModal = ({
  isOpen,
  cart,
  tableCart,
  store,
  table,
  checkout,
  me,
  order,
  kiosk,

  setIsTableCartModalOpen,
  setCheckout,
  setCheckoutBackup,
  setIsTipModalOpen,
  fetchOnAuthenticated,
  setOrder,
  setOrderHash,
  setCart,
  setIsCheckoutModalOpen,
}) => {
  const intl = useIntl();
  const reduxStore = useStore();
  const [isLoading, setIsLoading] = useState(false);
  const [showItemAddedAnimation, setShowItemAddedAnimation] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [selectedItems, setSelectedItems] = useState([]);
  const [customerProfile, setCustomerProfile] = useState(null);
  // Table => always Inhouse. Read flags from the inhouse OrderConfig (the
  // legacy `merchantProfile.enablePayAndGo / enableTips` columns and the
  // `merchantProfile.posIntegration` nesting were removed on the backend
  // — see migrations 1771528189413-MigrateMerchantProfileToOrderConfigs and
  // 1772090771682-RemoveLegacyMerchantProfileColumns).
  const inhouseOrderConfig = getActiveOrderConfig(
    store,
    ORDER_TYPE_INHOUSE,
    table,
    kiosk,
  );
  const enablePayAndGo = !!inhouseOrderConfig?.allowedCheckoutMethods?.includes(
    CHECKOUT_METHOD_PAY_AND_GO,
  );
  const enableTips = getTipSettings(inhouseOrderConfig).allowTip;
  const posIntegration =
    inhouseOrderConfig?.posIntegration || store?.posIntegration;
  const itemsTotal = calculateCartTotal(
    ORDER_TYPE_INHOUSE, // Table => always Inhouse
    tableCart?.items,
  );
  const selectedItemsTotal = calculateCartTotal(
    ORDER_TYPE_INHOUSE, // Table => always Inhouse
    selectedItems,
  );
  const socketContext = useContext(SocketContext);
  const formRef = useRef(null);
  // Support both old (me?.requester?.customerProfile) and new (me?.customerProfileId) API structures
  const customerProfileId = me?.customerProfileId || me?.requester?.customerProfile?.id;

  // Fetch customer profile when modal opens
  useEffect(() => {
    if (isOpen && customerProfileId && !customerProfile) {
      getCustomerProfile(customerProfileId)
        .then((profile) => {
          setCustomerProfile(profile);
        })
        .catch((err) => {
          console.error('Failed to fetch customer profile:', err);
        });
    }
  }, [isOpen, customerProfileId]);

  const isSelected = (cartItem) =>
    !!selectedItems?.find(
      ({ selectionId }) => selectionId === cartItem.selectionId,
    );

  const handleSelect = (cartItem) => {
    if (socketContext?.handleEmitSelectOrderItem && cartItem) {
      const isSelectedAction = isSelected(cartItem) ? 'unselect' : 'select';
      socketContext.handleEmitSelectOrderItem(
        cartItem.selectionId,
        isSelectedAction,
      );
    }

    return isSelected(cartItem)
      ? setSelectedItems(
          selectedItems.filter(
            ({ selectionId }) => selectionId !== cartItem.selectionId,
          ),
        )
      : setSelectedItems([...(selectedItems || []), cartItem]);
  };

  const handleUpdateTableCart = () => {
    if (!isUpdating) {
      setIsUpdating(true);
      let requestFinished = false;

      // check periodically if fetching is done
      setInterval(() => {
        if (requestFinished) {
          setIsUpdating(false);
          requestFinished = false;
        }
      }, 1000);

      const cb = () => {
        requestFinished = true;
      };

      return updateTableCart(store?.id, table?.id)
        .then(cb)
        .catch(cb);
    } else {
      console.log('TableCart update is already in progress');
      return Promise.reject();
    }
  };

  // periodically update the TableCart
  useEffect(() => {
    let intervalId;

    // Pull-based POS integrations (Matrix, ready2order) keep the tab in the POS
    // and don't push cart updates to us, so poll them in the background even
    // when the modal is closed.
    const pollInBackgroundPosTypes = [
      'MatrixPosIntegration',
      'Ready2OrderPosIntegration',
    ];
    if (isOpen || pollInBackgroundPosTypes.includes(posIntegration?.type)) {
      handleUpdateTableCart().then(() => {
        intervalId = setInterval(handleUpdateTableCart, 15000);
      });
    }

    return () => clearInterval(intervalId);
  }, [isOpen]);

  // set user selected items.
  // Compare against the customerProfileId derived from `me` (which is the
  // exact value the backend writes into `selectedBy.id` — see
  // table-cart.service.ts:412) instead of the locally fetched
  // `customerProfile?.id`, which is undefined until getCustomerProfile()
  // resolves. Without this, the first render after the modal opens
  // incorrectly classifies every selection as "selected by someone else".
  useEffect(() => {
    if (tableCart) {
      const itemsSelectedByUser = tableCart?.items?.filter(
        (cartItem) => cartItem?.selectedBy?.id === customerProfileId,
      );
      setSelectedItems(itemsSelectedByUser);
    }
  }, [tableCart, customerProfileId]);

  const handleCreateOrder = () => {
    const handleOpenNextModal = () => {
      if (enableTips) {
        setIsTipModalOpen(true);
        return;
      }
      // This is the inhouse table (pay_and_go) checkout. The notification
      // method was already collected when the items were added to the table
      // (order_only), so we don't prompt for it again here — go straight to
      // checkout.
      setIsCheckoutModalOpen(true);
    };

    // if order was already created, skip creation here, Order will be updated in checkout component
    // if the orderType has changed, we need to re-create the order
    if (!order || order.type !== checkout.orderType) {
      setIsLoading(true);

      // we need to back up the current checkout to restore it after
      const checkoutBackup = { ...checkout };
      setCheckoutBackup(checkout);

      // here, we already know that it will be Inhouse + Pay&Go. So pre-define it.
      const checkoutConfig = {
        orderType: ORDER_TYPE_INHOUSE,
        checkoutMethod: CHECKOUT_METHOD_PAY_AND_GO,
      };
      setCheckout(checkoutConfig);
      setCart(selectedItems);

      // create the Order
      createOrder(
        me,
        order,
        { ...checkout, ...checkoutConfig }, // redux is not fast enough, so merge
        selectedItems,
        store,
        setOrder,
        setOrderHash,
        fetchOnAuthenticated,
        intl,
        table,
        kiosk,
        socketContext?.setToken,
        () => reduxStore.getState().auth?.me, // get fresh me from Redux store
        customerProfile, // pass existing customer profile to avoid extra fetch
      )
        .then(() => {
          setIsLoading(false);
          handleOpenNextModal(); // opens tip or checkout modal
          setSelectedItems([]);
        })
        .catch((err) => {
          console.error(err);
          setCheckout(checkoutBackup);
          setCart(null);
          setIsLoading(false);
          setIsTableCartModalOpen(false);
          setSelectedItems([]);
          notificationFormattedMessage('error', getOrderErrorMessage(err), {
            storeName: store?.name,
            error: err?.response?.data?.error,
          });
        });
    } else {
      handleOpenNextModal();
    }
  };

  return (
    <>
      <ModalSheet
        footer={
          // currently, if payAndGo is disabled, the TableCart will not be shown at all
          <>
            {selectedItemsTotal > 0 && selectedItemsTotal < 0.5 && (
              <Alert className="d-flex p-50 mb-1" color="warning">
                <span className="font-small-3">
                  <FormattedMessage
                    id="minOrderAmountWarning"
                    values={{
                      storeName: store?.name,
                      minOrderAmount: intl.formatNumber(0.5, {
                        format: 'priceCurrency',
                      }),
                    }}
                  />
                </span>
              </Alert>
            )}
            {enablePayAndGo ? (
              <SaveSubmitButton
                data-testid="proceedToCheckoutTable"
                color="primary"
                className="btn-lg"
                disabled={
                  isLoading || selectedItemsTotal < 0.5 // total has to be >= 0,50€ because of Stripe
                }
                isSubmitting={isLoading}
                onClick={() => formRef?.current.handleSubmit()} // this button is outside the AvForm. So trigger submit programmatically.
                messageId="proceedToCheckoutLabel"
                messageValues={{
                  price: intl.formatNumber(selectedItemsTotal, {
                    format: 'priceCurrency',
                  }),
                }}
              />
            ) : (
              <span className="d-block text-center font-medium-1 mt-1">
                <FormattedMessage id="payAtWaiterLabel" />
              </span>
            )}
            <span className="d-block font-small-2 text-center mt-1">
              <FormattedMessage
                id="tableCartMessage"
                values={{ storeName: store?.name }}
              />
            </span>
          </>
        }
        isOpen={isOpen && tableCart?.items?.length > 0}
        closeModal={() => setIsTableCartModalOpen(false)}
        title={
          <FormattedMessage
            id="tableOrdersLabel"
            values={{ tableName: table?.name }}
          />
        }
        isRefreshing={isUpdating}
        onRefreshHandler={handleUpdateTableCart}
      >
        <AvForm ref={formRef} onValidSubmit={handleCreateOrder}>
          <div className="px-1">
            <span className="font-small-3">
              {enablePayAndGo ? (
                <FormattedMessage id="selectItemsYouPayLabel" />
              ) : (
                <FormattedMessage id="itemsInTableCartLabel" />
              )}
            </span>

            {tableCart?.items?.map((orderItem, index) => {
              const { itemData, additions, selectedBy } = orderItem;
              const _isSelected = isSelected(orderItem);
              const _handleSelect = () =>
                // if there is a PosIntegration, wait for the posIntegrationRef
                (!posIntegration || orderItem?.posIntegrationRef) &&
                (!selectedBy || selectedBy.id === customerProfileId)
                  ? handleSelect(orderItem)
                  : undefined;

              return (
                <>
                  <div
                    key={index}
                    style={{ transition: 'border 300ms', height: '44px' }}
                    data-testid="itemOnTableCart"
                    className={classnames(
                      'd-flex align-items-center my-50 px-75 py-50 rounded',
                      {
                        'border-primary': _isSelected,
                        'border-order-cart': !_isSelected,
                      },
                    )}
                    onClick={_handleSelect}
                  >
                    <div className="d-flex align-items-center flex-grow-0">
                      {enablePayAndGo &&
                      // if there is a PosIntegration, wait for the posIntegrationRef
                      (!posIntegration || orderItem?.posIntegrationRef) ? (
                        _isSelected ? (
                          <Icon.CheckSquare
                            className="vx-icon primary"
                            size={24}
                            style={{ minWidth: '24px' }}
                          />
                        ) : selectedBy &&
                          selectedBy?.id !== customerProfileId ? (
                          <Icon.XSquare
                            className="vx-icon danger"
                            size={24}
                            style={{ minWidth: '24px' }}
                          />
                        ) : (
                          <Icon.Square
                            data-testid="squareOnItem"
                            className="vx-icon"
                            size={24}
                            style={{ minWidth: '24px' }}
                          />
                        )
                      ) : (
                        <Spinner
                          color="primary"
                          style={{
                            height: '1.5rem',
                            width: '1.5rem',
                            minWidth: '1.5rem',
                          }}
                        />
                      )}
                      <LazyImage
                        image={itemData.image}
                        src={
                          itemData.image === null &&
                          '/assets/img/default_item_image_thumbnail.svg'
                        }
                        variant="thumbnail"
                        className="ml-50 thumbnail-30 img-rounded"
                        placeholderContainerClassNames="thumbnail-30"
                        placeholderClassNames="thumbnail-30"
                      />
                    </div>

                    <div className="flex-grow-1 d-flex flex-column px-75 truncate">
                      <div className="font-medium-1 line-height-125 black">
                        <Marquee>
                          {getLocalizedName(itemData, intl.locale)}
                        </Marquee>
                      </div>
                      <div className="truncate font-small-2">
                        {additions?.length > 0 && (
                          <Marquee>
                            {additions
                              ?.map(
                                (addition) =>
                                  (addition.qty > 1
                                    ? `${addition.qty}x `
                                    : '') +
                                  getLocalizedName(
                                    addition.itemAddition,
                                    intl.locale,
                                  ),
                              )
                              .join(', ')}
                          </Marquee>
                        )}
                      </div>
                    </div>

                    <div className="d-flex flex-column justify-content-between align-items-end">
                      <span className="font-medium-1 text-bold-500 black line-height-1">
                        <FormattedNumber
                          value={getOrderItemTotal(
                            ORDER_TYPE_INHOUSE,
                            orderItem,
                            true,
                          )}
                          format="priceCurrency"
                        />
                      </span>
                    </div>
                  </div>
                </>
              );
            })}
          </div>
        </AvForm>

        {/*<div className="d-flex justify-content-between text-bold-500 black px-1">
          <span className="font-medium-1">
            <FormattedMessage id="totalLabel" />
          </span>
          <span className="font-medium-1">
            <FormattedNumber
              value={selectedItemsTotal}
              format="priceCurrency"
            />
          </span>
        </div>*/}
      </ModalSheet>

      {enablePayAndGo && (
        <div
          data-testid="tableCartContainer"
          className={classnames('mini-cart-container', {
            active:
              tableCart?.items?.length > 0 &&
              !isOpen &&
              (!cart || cart.length === 0),
            'cart-changed': showItemAddedAnimation,
          })}
          onClick={() => setIsTableCartModalOpen(true)}
          onDrag={() => setIsTableCartModalOpen(true)}
          onAnimationEnd={() => setShowItemAddedAnimation(false)}
        >
          <h1 className="font-medium-1 m-0">
            <Grid size={18} className="mr-25" />
            <FormattedMessage
              id="tableCartTitleWithPriceLabel"
              values={{
                price: intl.formatNumber(itemsTotal, {
                  format: 'priceCurrency',
                }),
              }}
            />
          </h1>
        </div>
      )}
    </>
  );
};

const mapStateToProps = (state) => ({
  isOpen: state.modals.isTableCartModalOpen,
  cart: state.store.cart,
  tableCart: state.store.tableCart,
  tableCartSelectedItems: state.store.tableCartSelectedItems,
  store: state.store.store,
  table: state.store.table,
  order: state.store.order,
  checkout: state.store.checkout,
  me: state.me.me,
  kiosk: state.store.kiosk,
});

export default connect(mapStateToProps, {
  setIsTableCartModalOpen,
  setCheckout,
  setIsTipModalOpen,
  fetchOnAuthenticated,
  setOrder,
  setOrderHash,
  setCart,
  setSpecificOrder,
  setIsCheckoutModalOpen,
  setCheckoutBackup,
})(TableCartModal);
